import { useEffect, useState } from "react";

import { useUser } from "@ui/hooks/useUser";

import { TssApi } from "@jti/rest-api";

export type TrialStatus = { eligibleCategoryIds: string[]; isLoading: boolean; isTrialAvailable: boolean };
type UseTrialStatus = ({ isTrial, categoryIds }: { isTrial?: boolean; categoryIds?: string[] }) => TrialStatus;

export const useTrialStatus: UseTrialStatus = ({ isTrial = true, categoryIds = [] }) => {
  const [eligibleCategoryIds, setEligibleCategoryIds] = useState<string[] | null>(null);
  const { isUserLoggedIn, user } = useUser();

  useEffect(() => {
    if (!isTrial || categoryIds.length === 0) {
      setEligibleCategoryIds([]);
      return;
    }

    // Trial should be available for anonymous users
    if (isUserLoggedIn === false || user?.uuid === null) {
      setEligibleCategoryIds(categoryIds);
      return;
    }

    const tssApi = TssApi.init();
    const eligibleCatgoriesPromises = categoryIds.map(async (categoryId) => {
      const isEligible = await tssApi.getEligibility({ categoryId, userId: user?.uuid ?? "" });
      return isEligible ? categoryId : null;
    });

    Promise.all(eligibleCatgoriesPromises).then((ids) => {
      const eligibleCategoryIds = ids.filter((id): id is string => id !== null);
      setEligibleCategoryIds(eligibleCategoryIds);
    });
  }, [isTrial, isUserLoggedIn, user?.uuid, categoryIds]);

  const isLoading = eligibleCategoryIds === null;
  const isTrialAvailable = !isLoading && eligibleCategoryIds.length > 0;

  return {
    eligibleCategoryIds: eligibleCategoryIds ?? [],
    isLoading,
    isTrialAvailable,
  };
};
